// WordPress dependencies
import { useState, useEffect, useCallback } from '@wordpress/element';
import {
	Panel,
	PanelBody,
	PanelRow,
	Button,
	TextControl,
	SelectControl,
	Spinner,
} from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';

/**
 * Slider Item Component
 */
const SliderItem = ( { slider, isSelected, onAdd, onRemove, onDragStart, onDragEnd, onDragOver, onDrop } ) => {
	const thumbnailUrl = slider.thumbnail || window.COCA_BAIS_CAROUSEL_ASSETS?.placeholder || '';
	
	// Determine type icon based on slider type
	let typeIcon = 'format-image'; // Default image icon
	let typeLabel = __( 'Image Slider', 'wp-before-after-image-slider' );
	
	if ( slider.type === 'youtube' ) {
		typeIcon = 'video-alt3';
		typeLabel = __( 'YouTube Slider', 'wp-before-after-image-slider' );
	} else if ( slider.type === 'upload_video' ) {
		typeIcon = 'video-alt2';
		typeLabel = __( 'Video Slider', 'wp-before-after-image-slider' );
	}
	
	return (
		<div
			className={ `bais-slider-item ${ isSelected ? 'selected' : '' }` }
			data-slider-id={ slider.id }
			draggable={ isSelected }
			onDragStart={ onDragStart }
			onDragEnd={ onDragEnd }
			onDragOver={ onDragOver }
			onDrop={ onDrop }
		>
			<div className="bais-slider-thumbnail">
				{ slider.type === 'upload_video' && slider.video_url ? (
					<video width="100%" height="100%" muted>
						<source src={ slider.video_url } type="video/mp4" />
						Your browser does not support the video tag.
					</video>
				) : thumbnailUrl ? (
					<img src={ thumbnailUrl } alt={ slider.title } />
				) : (
					<span className="dashicons dashicons-format-image"></span>
				) }
				<span 
					className={ `bais-slider-type-indicator dashicons dashicons-${ typeIcon }` }
					title={ typeLabel }
				></span>
			</div>
			<div className="bais-slider-info">
				<h4>{ slider.title }</h4>
				<span className="bais-slider-id">#{ slider.id }</span>
			</div>
			<div className="bais-slider-actions">
				{ isSelected ? (
					<>
						<Button
							icon="no-alt"
							isSmall
							onClick={ () => onRemove( slider.id ) }
							label={ __( 'Remove', 'wp-before-after-image-slider' ) }
						/>
						<span className="bais-drag-handle dashicons dashicons-menu"></span>
					</>
				) : (
					<Button
						icon="plus-alt"
						isSmall
						onClick={ () => onAdd( slider ) }
						label={ __( 'Add to Carousel', 'wp-before-after-image-slider' ) }
					/>
				) }
			</div>
		</div>
	);
};

/**
 * Sliders Tab Component
 */
const SlidersTab = ( props ) => {
	const { attributes, updateAttributes } = props;
	const [ availableSliders, setAvailableSliders ] = useState( [] );
	const [ selectedSliders, setSelectedSliders ] = useState( [] );
	const [ searchTerm, setSearchTerm ] = useState( '' );
	const [ sliderTypeFilter, setSliderTypeFilter ] = useState( 'image' );
	const [ isLoading, setIsLoading ] = useState( true );
	const [ draggedIndex, setDraggedIndex ] = useState( null );

	// Load available sliders on mount
	useEffect( () => {
		fetchSliders();
	}, [] );

	// Initialize selected sliders from attributes
	useEffect( () => {
		if ( attributes.selected_slider_ids && availableSliders.length > 0 ) {
			const selected = attributes.selected_slider_ids
				.map( ( id ) => availableSliders.find( ( s ) => s.id === id ) )
				.filter( Boolean );
			setSelectedSliders( selected );
		}
	}, [ attributes.selected_slider_ids, availableSliders ] );

	// Fetch all available sliders
	const fetchSliders = async () => {
		setIsLoading( true );
		try {
			const response = await apiFetch( {
				path: '/coca-bais/v1/sliders',
				method: 'GET',
			} );
			setAvailableSliders( response || [] );
		} catch ( error ) {
			console.error( 'Error fetching sliders:', error );
			setAvailableSliders( [] );
		}
		setIsLoading( false );
	};

	// Add slider to selection
	const handleAddSlider = useCallback( ( slider ) => {
		const newSelected = [ ...selectedSliders, slider ];
		setSelectedSliders( newSelected );
		updateAttributes( {
			selected_slider_ids: newSelected.map( ( s ) => s.id ),
		} );
	}, [ selectedSliders, updateAttributes ] );

	// Remove slider from selection
	const handleRemoveSlider = useCallback( ( sliderId ) => {
		const newSelected = selectedSliders.filter( ( s ) => s.id !== sliderId );
		setSelectedSliders( newSelected );
		updateAttributes( {
			selected_slider_ids: newSelected.map( ( s ) => s.id ),
		} );
	}, [ selectedSliders, updateAttributes ] );

	// Drag and Drop handlers
	const handleDragStart = ( e, index ) => {
		setDraggedIndex( index );
		e.dataTransfer.effectAllowed = 'move';
	};

	const handleDragEnd = () => {
		setDraggedIndex( null );
	};

	const handleDragOver = ( e ) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = 'move';
	};

	const handleDrop = ( e, dropIndex ) => {
		e.preventDefault();
		if ( draggedIndex === null || draggedIndex === dropIndex ) return;

		const newSelected = [ ...selectedSliders ];
		const [ draggedItem ] = newSelected.splice( draggedIndex, 1 );
		newSelected.splice( dropIndex, 0, draggedItem );

		setSelectedSliders( newSelected );
		setDraggedIndex( null );
		updateAttributes( {
			selected_slider_ids: newSelected.map( ( s ) => s.id ),
		} );
	};

	// Filter available sliders (exclude already selected and apply search)
	const filteredAvailable = availableSliders.filter( ( slider ) => {
		const isSelected = selectedSliders.some( ( s ) => s.id === slider.id );
		const matchesSearch = 
			slider.title.toLowerCase().includes( searchTerm.toLowerCase() ) ||
			slider.id.toString().includes( searchTerm );
		
		// Apply slider type filter
		const matchesTypeFilter = 
			sliderTypeFilter === 'all' ||
			( sliderTypeFilter === 'image' && slider.type === 'image' ) ||
			( sliderTypeFilter === 'youtube' && slider.type === 'youtube' ) ||
			( sliderTypeFilter === 'upload_video' && slider.type === 'upload_video' );

		return ! isSelected && matchesSearch && matchesTypeFilter;
	} );

	return (
		<Panel>
			<PanelBody
				title={ __( 'Slider Selection', 'wp-before-after-image-slider' ) }
				initialOpen={ true }
			>
				<PanelRow>
					<div className="bais-slider-selection-wrapper">
						<div className="bais-slider-filters">
						<div className="bais-search-wrapper">
							<TextControl
								className="bais-slider-search"
								placeholder={ __( 'Search sliders...', 'wp-before-after-image-slider' ) }
								value={ searchTerm }
								onChange={ setSearchTerm }
							/>
							{ searchTerm && (
								<Button
									className="bais-search-clear"
									icon="no-alt"
									isSmall
									onClick={ () => setSearchTerm( '' ) }
									label={ __( 'Clear search', 'wp-before-after-image-slider' ) }
								/>
							) }
						</div>
							<SelectControl
								className="bais-slider-type-filter"
								value={ sliderTypeFilter }
								onChange={ setSliderTypeFilter }
								options={ [
									{ label: __( 'All', 'wp-before-after-image-slider' ), value: 'all' },
									{ label: __( 'Image', 'wp-before-after-image-slider' ), value: 'image' },
									{ label: __( 'YouTube', 'wp-before-after-image-slider' ), value: 'youtube' },
									{ label: __( 'Upload Video', 'wp-before-after-image-slider' ), value: 'upload_video' },
								] }
							/>
						</div>
						
						<div className="bais-slider-columns">
							{/* Available Sliders */}
							<div className="bais-available-sliders">
								<h3>{ __( 'Available Sliders', 'wp-before-after-image-slider' ) }</h3>
								<div className="bais-slider-list">
									{ isLoading ? (
										<div className="bais-loading-sliders">
											<Spinner />
											<p>{ __( 'Loading sliders...', 'wp-before-after-image-slider' ) }</p>
										</div>
									) : filteredAvailable.length > 0 ? (
										filteredAvailable.map( ( slider ) => (
											<SliderItem
												key={ slider.id }
												slider={ slider }
												isSelected={ false }
												onAdd={ handleAddSlider }
											/>
										) )
									) : (
										<p className="bais-no-sliders">
											{ searchTerm 
												? __( 'No sliders match your search.', 'wp-before-after-image-slider' )
												: __( 'No sliders available.', 'wp-before-after-image-slider' )
											}
										</p>
									) }
								</div>
							</div>

							{/* Selected Sliders */}
							<div className="bais-selected-sliders">
								<h3>{ __( 'Selected Sliders (Drag to Reorder)', 'wp-before-after-image-slider' ) }</h3>
								<div className="bais-slider-list bais-sortable">
									{ selectedSliders.length > 0 ? (
										selectedSliders.map( ( slider, index ) => (
											<SliderItem
												key={ slider.id }
												slider={ slider }
												isSelected={ true }
												onRemove={ handleRemoveSlider }
												onDragStart={ ( e ) => handleDragStart( e, index ) }
												onDragEnd={ handleDragEnd }
												onDragOver={ handleDragOver }
												onDrop={ ( e ) => handleDrop( e, index ) }
											/>
										) )
									) : (
										<p className="bais-no-sliders">
											{ __( 'No sliders selected. Add sliders from the left panel.', 'wp-before-after-image-slider' ) }
										</p>
									) }
								</div>
							</div>
						</div>
					</div>
				</PanelRow>
			</PanelBody>
		</Panel>
	);
};

export default SlidersTab;
